/**
 * @fileoverview Optimization History Service
 * Handles archiving and retrieval of historical optimization jobs
 * Extracted from EnhancedBacktestService for better separation of concerns
 */

import { apiClient } from '../api/client';
import { API_ENDPOINTS } from '../api/endpoints';
import type { OptimizationJob } from './OptimizationEventService';

export interface HistoricalJobsOptions {
  page?: number;
  pageSize?: number;
  status?: OptimizationJob['status'] | 'all' | 'archived';
  search?: string;
  includeArchived?: boolean;
}

export interface BulkArchiveOptions {
  status?: OptimizationJob['status'] | 'all' | 'archived';
  search?: string;
  includeArchived?: boolean;
  strategy?: string;
  symbol?: string;
  interval?: string;
}

export interface HistoricalJobsResponse {
  jobs: OptimizationJob[];
  total: number;
  page: number;
  pageSize: number;
  totalPages?: number;
  hasMore?: boolean;
}

export interface BulkArchiveResponse {
  archivedCount: number;
  matchedCount: number;
}

export class OptimizationHistoryService {
  /**
   * Get historical completed jobs from database
   */
  async getHistoricalJobs(options: HistoricalJobsOptions = {}): Promise<HistoricalJobsResponse> {
    const {
      page: requestedPage = 1,
      pageSize: requestedPageSize = 25,
      status = 'all',
      search = '',
      includeArchived = false
    } = options;

    const params = new URLSearchParams();
    params.set('page', Math.max(requestedPage, 1).toString());
    params.set('pageSize', Math.max(requestedPageSize, 1).toString());

    if (status && status !== 'all') {
      params.set('status', status);
    }

    if (search && search.trim().length > 0) {
      params.set('search', search.trim());
    }

    if (includeArchived) {
      params.set('includeArchived', 'true');
    }

    try {
      const endpoint = API_ENDPOINTS.OPTIMIZATION.HISTORICAL.LIST;
      const url = `${endpoint}?${params.toString()}`;
      const response = await apiClient.get<any>(url);

      if (response && typeof response === 'object' && 'success' in response && response.success === false) {
        throw new Error(response.error || 'Failed to fetch historical jobs');
      }

      const payload = response && typeof response === 'object' && 'data' in response && response.data
        ? response.data
        : response;

      const jobsPayload = payload && typeof payload === 'object' ? payload : {};
      const rawJobs = Array.isArray(jobsPayload.jobs)
        ? jobsPayload.jobs
        : Array.isArray(response?.jobs)
          ? response.jobs
          : [];

      const normalizedJobs = (rawJobs as unknown[])
        .map((job: unknown) => this.normalizeHistoricalJob(job))
        .filter((job): job is OptimizationJob => job !== null);

      const total = this.coerceNumber(jobsPayload.total ?? response?.total) ?? normalizedJobs.length;

      const resolvedPageSize = this.coerceNumber(
        jobsPayload.pageSize ?? jobsPayload.limit ?? response?.pageSize ?? response?.limit
      ) ?? requestedPageSize;

      const resolvedPage = this.coerceNumber(jobsPayload.page ?? response?.page) ?? (
        (() => {
          const offset = this.coerceNumber(jobsPayload.offset ?? response?.offset);
          if (offset == null || resolvedPageSize <= 0) {
            return requestedPage;
          }
          return Math.floor(offset / resolvedPageSize) + 1;
        })()
      );

      const safePageSize = resolvedPageSize > 0 ? resolvedPageSize : Math.max(requestedPageSize, 1);

      const totalPagesFromPayload = this.coerceNumber(jobsPayload.totalPages ?? response?.totalPages);
      const totalPages = totalPagesFromPayload && totalPagesFromPayload > 0
        ? Math.ceil(totalPagesFromPayload)
        : safePageSize > 0
          ? Math.max(Math.ceil(total / safePageSize), 1)
          : 1;
      const normalizedPage = resolvedPage > 0 ? resolvedPage : 1;
      const hasMoreFromPayload = typeof jobsPayload.hasMore === 'boolean' ? jobsPayload.hasMore : response?.hasMore;
      const hasMore = typeof hasMoreFromPayload === 'boolean' ? hasMoreFromPayload : normalizedPage < totalPages;

      return {
        jobs: normalizedJobs,
        total,
        page: normalizedPage,
        pageSize: safePageSize,
        totalPages,
        hasMore
      };
    } catch (error) {
      return {
        jobs: [],
        total: 0,
        page: requestedPage,
        pageSize: requestedPageSize,
        totalPages: 1,
        hasMore: false
      };
    }
  }

  /**
   * Archive (soft delete) a single historical optimization job
   */
  async archiveJob(jobId: string): Promise<void> {
    if (!jobId) {
      throw new Error('Job ID is required to archive a historical job');
    }

    try {
      const endpoint = API_ENDPOINTS.OPTIMIZATION.HISTORICAL.ARCHIVE(jobId);
      const response = await apiClient.delete<any>(endpoint);

      if (response && typeof response === 'object') {
        if ('success' in response && response.success === false) {
          throw new Error(response.error || 'Failed to archive optimization job');
        }
        return;
      }

      // If response format unknown but request succeeded, consider archived
    } catch (error) {
      throw (error instanceof Error ? error : new Error('Failed to archive optimization job'));
    }
  }

  /**
   * Get a single historical optimization job by ID
   */
  async getHistoricalJob(jobId: string): Promise<OptimizationJob | null> {
    if (!jobId) {
      throw new Error('Job ID is required to fetch a historical job');
    }

    try {
      const endpoint = API_ENDPOINTS.OPTIMIZATION.HISTORICAL.GET(jobId);
      const response = await apiClient.get<any>(endpoint);

      if (response && typeof response === 'object') {
        if ('success' in response && response.success === false) {
          throw new Error(response.error || 'Failed to fetch historical job');
        }

        const payload = 'data' in response && response.data ? response.data : response;
        const normalizedJob = this.normalizeHistoricalJob(payload);

        if (normalizedJob) {
        }

        return normalizedJob;
      }

      return null;
    } catch (error) {
      throw (error instanceof Error ? error : new Error('Failed to fetch historical job'));
    }
  }

  /**
   * Archive (soft delete) a collection of historical optimization jobs in bulk
   */
  async archiveBulk(options: BulkArchiveOptions = {}): Promise<BulkArchiveResponse> {
    const {
      status = 'all',
      search = '',
      includeArchived = false,
      strategy,
      symbol,
      interval
    } = options;

    const params = new URLSearchParams();

    if (status && status !== 'all') {
      params.set('status', status);
    }

    if (search && search.trim().length > 0) {
      params.set('search', search.trim());
    }

    if (includeArchived) {
      params.set('includeArchived', 'true');
    }

    if (strategy && strategy.trim().length > 0) {
      params.set('strategy', strategy.trim());
    }

    if (symbol && symbol.trim().length > 0) {
      params.set('symbol', symbol.trim());
    }

    if (interval && interval.trim().length > 0) {
      params.set('interval', interval.trim());
    }

    const endpoint = API_ENDPOINTS.OPTIMIZATION.HISTORICAL.BULK_ARCHIVE;
    const url = params.size > 0 ? `${endpoint}?${params.toString()}` : endpoint;

    try {
      const response = await apiClient.delete<any>(url);

      if (response && typeof response === 'object') {
        if ('success' in response && response.success === false) {
          throw new Error(response.error || 'Failed to archive optimization jobs');
        }

        const payload = 'data' in response && response.data
          ? response.data
          : response;

        if (payload && typeof payload === 'object') {
          const archivedCount = this.coerceNumber(payload.archivedCount) ?? 0;
          const matchedCount = this.coerceNumber(payload.matchedCount) ?? archivedCount;
          return { archivedCount, matchedCount };
        }
      }

      return { archivedCount: 0, matchedCount: 0 };
    } catch (error) {
      throw (error instanceof Error ? error : new Error('Failed to archive optimization jobs'));
    }
  }

  /**
   * Utility: Coerce value to number or return undefined
   */
  private coerceNumber(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }

    return undefined;
  }

  /**
   * Utility: Resolve date to ISO string or return undefined
   */
  private resolveDate(value: unknown): string | undefined {
    if (!value) {
      return undefined;
    }

    const date = value instanceof Date ? value : new Date(value as string);
    if (Number.isNaN(date.getTime())) {
      return undefined;
    }

    return date.toISOString();
  }

  /**
   * Utility: Normalize raw job data to OptimizationJob type
   */
  private normalizeHistoricalJob(job: unknown): OptimizationJob | null {
    if (!job || typeof job !== 'object') {
      return null;
    }

    const jobRecord = job as Record<string, unknown>;
    const metadata = typeof jobRecord.metadata === 'object' && jobRecord.metadata !== null
      ? jobRecord.metadata as Record<string, unknown>
      : undefined;

    const idCandidate = jobRecord.id ?? jobRecord.jobId ?? jobRecord._id;
    if (!idCandidate) {
      return null;
    }

    const statusValue = (jobRecord.status ?? metadata?.status ?? 'completed') as OptimizationJob['status'];

    const progressValue = this.coerceNumber(jobRecord.progress) ?? this.coerceNumber(metadata?.progress) ?? 0;
    const currentValue = this.coerceNumber(jobRecord.current) ?? this.coerceNumber(metadata?.current) ?? 0;
    const totalValue = this.coerceNumber(jobRecord.total) ?? this.coerceNumber(metadata?.total) ?? 0;

    const startTime = this.resolveDate(jobRecord.startTime ?? jobRecord.createdAt ?? metadata?.startTime);
    const completedAt = this.resolveDate(jobRecord.completedAt ?? jobRecord.completedTime ?? metadata?.completedAt ?? jobRecord.updatedAt);
    const estimatedCompletion = this.resolveDate(jobRecord.estimatedCompletion ?? metadata?.estimatedCompletion);
    const createdAt = this.resolveDate(jobRecord.createdAt ?? metadata?.createdAt);
    const updatedAt = this.resolveDate(jobRecord.updatedAt ?? metadata?.updatedAt);
    const archivedAt = this.resolveDate(jobRecord.archivedAt ?? metadata?.archivedAt);

    const isArchivedRaw = jobRecord.isArchived ?? metadata?.isArchived ?? false;
    const isArchived = typeof isArchivedRaw === 'string'
      ? isArchivedRaw.toLowerCase() === 'true'
      : Boolean(isArchivedRaw);

    const estimatedDuration = this.coerceNumber(jobRecord.estimatedDuration) ?? this.coerceNumber(metadata?.estimatedDuration);
    const estimatedTimeRemaining = this.coerceNumber(jobRecord.estimatedTimeRemaining) ?? this.coerceNumber(metadata?.estimatedTimeRemaining);
    const queuePosition = this.coerceNumber(jobRecord.queuePosition) ?? this.coerceNumber(metadata?.queuePosition);

    const bestResult = jobRecord.bestResult
      ?? metadata?.bestResult
      ?? (Array.isArray(jobRecord.results) ? jobRecord.results[0] : undefined);

    // Get allResults from allResults field, results field, or metadata
    const allResults = (jobRecord.allResults ?? jobRecord.results ?? metadata?.allResults ?? []) as Array<{
      parameters: Record<string, number>;
      metrics: Record<string, number>;
      score: number;
    }>;

    const normalizedJob: OptimizationJob = {
      id: String(idCandidate),
      status: statusValue,
      strategy: (jobRecord.strategy ?? metadata?.strategy ?? 'Unknown Strategy') as string,
      symbol: (jobRecord.symbol ?? metadata?.symbol ?? 'UNKNOWN') as string,
      interval: (jobRecord.interval ?? metadata?.interval ?? 'unknown') as string,
      progress: progressValue,
      current: currentValue,
      total: totalValue,
      startTime: startTime ?? new Date().toISOString(),
      completedAt: completedAt,
      estimatedCompletion,
      estimatedDuration,
      estimatedTimeRemaining,
      bestResult,
      allResults,
      isArchived,
      archivedAt,
      metadata: metadata || undefined, // Preserve original metadata
    };

    return normalizedJob;
  }
}

// Export singleton instance
export const optimizationHistoryService = new OptimizationHistoryService();
